import PropTypes from 'prop-types';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Box,
    Button,
    Menu
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import DynamicRaceNav from './DynamicRaceNav';
import DynamicSubmenuItem from './DynamicSubmenuItem';

function NavSubmenuItem({ item, index, isOpen, anchorEl, onOpen, onClose, showTransparent, isItemRegistered }) {
    const hasDivider = Boolean(item.submenu?.some(i => i.isDivider));

    return (
        <Box key={item.label}>
            <Button
                id={`nav-menu-button-${index}`}
                aria-controls={isOpen ? `nav-menu-${index}` : undefined}
                aria-haspopup="true"
                aria-expanded={isOpen}
                onClick={onOpen}
                endIcon={<ExpandMore sx={{ fontSize: '0.9rem !important' }} />}
                sx={{
                    color: showTransparent ? 'white' : 'text.primary',
                    fontWeight: 600,
                    fontSize: '0.825rem',
                    py: 0.5,
                    px: 1.25,
                    '&:hover': {
                        bgcolor: showTransparent
                            ? 'rgba(255,255,255,0.1)'
                            : 'rgba(0,0,0,0.04)',
                    },
                }}
            >
                {item.label}
            </Button>
            <Menu
                id={`nav-menu-${index}`}
                aria-labelledby={`nav-menu-button-${index}`}
                anchorEl={anchorEl}
                open={isOpen}
                onClose={onClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                sx={{
                    '& .MuiPaper-root': {
                        borderRadius: 2,
                        mt: 1,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    },
                }}
            >
                {item.submenu?.map((subItem, subIndex) => (
                    <DynamicSubmenuItem
                        key={subItem.label || `submenu-${subIndex}`}
                        subItem={subItem}
                        subIndex={subIndex}
                        hasDivider={hasDivider}
                        onClose={onClose}
                        isItemRegistered={isItemRegistered}
                    />
                ))}
            </Menu>
        </Box>
    );
}

NavSubmenuItem.propTypes = {
    item: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
    isOpen: PropTypes.bool,
    anchorEl: PropTypes.any,
    onOpen: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    showTransparent: PropTypes.bool,
    isItemRegistered: PropTypes.func,
};

/**
 * NavLinks Component
 * Renders desktop primary navigation items and active route styling.
 * Delegates race navigation to DynamicRaceNav per ADR 0005.
 */
export function NavLinks({ items = [], showTransparent, isItemRegistered }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [openSubmenu, setOpenSubmenu] = useState(null);

    const handleMenuOpen = (event, index) => {
        setAnchorEl(event.currentTarget);
        setOpenSubmenu(index);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setOpenSubmenu(null);
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {items.map((item, index) => {
                // Delegate Camps & Races to DynamicRaceNav per ADR 0005
                if (item.label === 'Camps & Races' || item.label === 'Races') {
                    return (
                        <DynamicRaceNav
                            key={item.label}
                            item={item}
                            index={index}
                            isOpen={openSubmenu === index}
                            anchorEl={anchorEl}
                            onOpen={(e) => handleMenuOpen(e, index)}
                            onClose={handleMenuClose}
                            showTransparent={showTransparent}
                            isItemRegistered={isItemRegistered}
                        />
                    );
                }

                if (item.submenu) {
                    return (
                        <NavSubmenuItem
                            key={item.label}
                            item={item}
                            index={index}
                            isOpen={openSubmenu === index}
                            anchorEl={anchorEl}
                            onOpen={(e) => handleMenuOpen(e, index)}
                            onClose={handleMenuClose}
                            showTransparent={showTransparent}
                            isItemRegistered={isItemRegistered}
                        />
                    );
                }

                return (
                    <Box key={item.label}>
                        <Button
                            component={Link}
                            to={item.path}
                            sx={{
                                color: showTransparent ? 'white' : 'text.primary',
                                fontWeight: 600,
                                fontSize: '0.825rem',
                                py: 0.5,
                                px: 1.25,
                                textShadow: showTransparent ? '1px 1px 2px rgba(0,0,0,0.3)' : 'none',
                                '&:hover': {
                                    bgcolor: showTransparent
                                        ? 'rgba(255,255,255,0.1)'
                                        : 'rgba(0,0,0,0.04)',
                                },
                            }}
                        >
                            {item.label}
                        </Button>
                    </Box>
                );
            })}
        </Box>
    );
}

NavLinks.propTypes = {
    items: PropTypes.arrayOf(PropTypes.object),
    showTransparent: PropTypes.bool,
    isItemRegistered: PropTypes.func,
};

export default NavLinks;
